import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, Injector, ModuleWithProviders, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { Store, provideStore } from '@ngxs/store';
import { HttpClientInMemoryWebApiModule, InMemoryBackendConfigArgs } from 'angular-in-memory-web-api';
import { RecaptchaV3Module } from 'ng-recaptcha-2';
import { provideMatomo, withRouter } from 'ngx-matomo-client';

import {
    DEFAULT_NAVIGATION_CONFIG,
    InitAppService,
    McaAppState,
    ModalBookingConfirmationService,
    SelfBookingServiceClientService,
    bookingFiltersConfig,
} from '@mca-feature';
import {
    AppBasicsModuleConfigModel,
    AuthState,
    USER_INITIALIZATION_SERVICE,
    provideAppBasics,
    provideMdsModal,
    provideMfCommonAuth,
} from '@meddev/fe-shared';
import { MfCaptchaStrategyEnum, provideMfCaptcha } from '@meddev/fe-shared/mf-captcha';
import { BOOKING_CONFIRMATION_SERVICE, BOOKING_FILTER_CONFIG, BOOKING_SERVICE_TOKEN, SelfBookingState } from '@shared/booking-feature';
import {
    APP_CONSTANTS,
    APP_ROUTE_CONSTANTS,
    AuthGuardConfig,
    BaseInitAppService,
    CustomDateParserFormatter,
    GUARD_CONFIG,
    LANGUAGE_SWITCHER_CONFIG,
    LOGIN_LOGO_COMPONENT,
    MENU_ITEMS,
    NAVIGATION_CONFIG,
    NgbDatepickerAdapterService,
    SharedAppState,
    appInitFactory,
} from '@shared/core';
import { MCAAuthState, initAuthManagerFactory } from '@shared/data';
import { AUTH_MANAGER_BASE_TOKEN } from '@shared/data/misc/injection-tokens';
import { MESSAGING_ADAPTER, MessagingService, WsMessagingAdapterService } from '@shared/messaging-feature';
import { AgoraMediaAdapterService, CallState, MEDIA_ADAPTER, MediaManagerService, MediaState } from '@shared/telemedicine-feature';

import { provideRouter } from '@angular/router';
import { ApmModule } from '@elastic/apm-rum-angular';
import { InMemoryDataService } from '@mca-feature';
import { EnvironmentHelper } from '@shared/utils';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { LogoWrapperComponent } from './components/logo-wrapper/logo-wrapper.component';
import { appConstants } from './core/constant/constants';
import { appRouteConstants } from './core/constant/route-constants';
import { createMenuItems } from './core/factory/menu-items.factory';
import { McaUserInitializationService } from './service/user-initialization.service';

EnvironmentHelper.initEnvironment(environment);

function getInMemoryImports(): ModuleWithProviders<HttpClientInMemoryWebApiModule>[] {
    return !appConstants.environment.production && appConstants.environment.inMemory
        ? [HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, { delay: 1500 } as InMemoryBackendConfigArgs)]
        : [];
}

const inMemoryImports = getInMemoryImports();

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        provideStore([SharedAppState, AuthState, MCAAuthState, SelfBookingState, McaAppState, MediaState, CallState], {
            developmentMode: !environment.production,
        }),
        withNgxsRouterPlugin(),
        withNgxsLoggerPlugin(),
        withNgxsReduxDevtoolsPlugin(),
        withNgxsStoragePlugin({ keys: ['AuthState', 'MCAAuthState'] }),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideAppInitializer(() => {
            const initService = inject(BaseInitAppService);
            return appInitFactory(initService)();
        }),
        importProvidersFrom(ApmModule, RecaptchaV3Module, ...inMemoryImports),
        provideMatomo(
            {
                mode: 'deferred',
            },
            withRouter(),
        ),
        provideAppBasics({
            version: environment.VERSION as string,
            production: environment.production,
            releaseStage: environment.RELEASE_STAGE as AppBasicsModuleConfigModel['releaseStage'],
            bugSnag: { apiKey: environment.BUGSNAG_KEY },
            matomo: {
                siteId: 32,
                dimensions: { version: 1, majorVersion: 2, businessSubjectId: 3 },
            },
            apmServiceName: 'mca-app',
        }),
        provideMfCaptcha({
            language: 'en',
            v2: environment.reCaptcha?.v2,
            v3: environment.reCaptcha?.v3,
            defaultStrategy: MfCaptchaStrategyEnum.FALLBACK,
        }),
        provideMdsModal({
            default: {
                confirmButtonText: $localize`Confirm`,
                cancelButtonText: $localize`Close`,
                denyButtonText: $localize`Cancel`,
            },
            error: { title: $localize`Error` },
        }),
        provideMfCommonAuth({
            authV1Url: appConstants.environment.api.url + appConstants.httpClient.authBaseV1,
            authV2Url: appConstants.environment.api.url + appConstants.httpClient.authBase,
            authV2TokenPatterns: [/.*/],
            loginRedirectUrl: '/login',
            realmName: 'MCL',
            clientId: 'mcl',
            impersonationTokenUrlParamName: 'impersonation-token',
        }),
        { provide: BaseInitAppService, useClass: InitAppService },
        { provide: USER_INITIALIZATION_SERVICE, useClass: McaUserInitializationService },
        { provide: AUTH_MANAGER_BASE_TOKEN, useFactory: initAuthManagerFactory, deps: [Injector, Store], multi: false },
        {
            provide: GUARD_CONFIG,
            useValue: {
                loginRoute: appRouteConstants.login,
                redirectAfterLoginParameter: appConstants.redirectAfterLoginParameter,
            } as AuthGuardConfig,
        },
        { provide: APP_ROUTE_CONSTANTS, useValue: appRouteConstants },
        { provide: APP_CONSTANTS, useValue: appConstants },
        { provide: LOGIN_LOGO_COMPONENT, useValue: LogoWrapperComponent },
        { provide: MENU_ITEMS, useFactory: createMenuItems, deps: [BaseInitAppService] },
        { provide: BOOKING_SERVICE_TOKEN, useClass: SelfBookingServiceClientService },
        { provide: BOOKING_FILTER_CONFIG, useValue: bookingFiltersConfig },
        { provide: BOOKING_CONFIRMATION_SERVICE, useClass: ModalBookingConfirmationService },
        { provide: NAVIGATION_CONFIG, useFactory: () => inject(DEFAULT_NAVIGATION_CONFIG) },
        { provide: LANGUAGE_SWITCHER_CONFIG, useValue: { enabled: true } },
        { provide: NgbDateAdapter, useClass: NgbDatepickerAdapterService },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: MESSAGING_ADAPTER, useClass: WsMessagingAdapterService },
        MessagingService,
        { provide: MEDIA_ADAPTER, useClass: AgoraMediaAdapterService },
        MediaManagerService,
        provideAnimations(),
    ],
};
